import UserProductGroupNotificationSettings from '~/Api/User/ProductGroup/NotificationSettings/UserProductGroupNotificationSettings';
import { TimePeriod } from '../ExchangeRate';

import ExchangeRates from './ExchangeRates';
import { RatesSettings } from './ExchangeRatesSettings';
import SystemModule, { ModuleType } from './SystemModule';
import UserProductGroupNotification from './UserProductGroupNotification';
import UserProductGroupParent from './UserProductGroupParent';

export const PRODUCT_LIST_TYPE = 'product-list';
export const GROUP_TYPE = 'group';

export default class UserProductGroup {
	id: string;

	name: string;

	productsCount: number;

	productGroupsCount: number;

	itemsUpdatedTime: Date;

	notification: UserProductGroupNotification;

	type: string;

	systemModule: SystemModule | null;

	parent: UserProductGroupParent | null;

	exchangeRates: ExchangeRates;

	historicProductDataViewDate: Date | null;

	links: { [key: string]: string | undefined };

	constructor(
		id: string,
		name: string,
		productsCount: number,
		productGroupsCount: number,
		itemsUpdatedTime: Date,
		notification: UserProductGroupNotification,
		type: string,
		systemModule: SystemModule | null,
		parent: UserProductGroupParent | null,
		exchangeRates: ExchangeRates,
		historicProductDataViewDate: Date | null,
		links: { [key: string]: string | undefined }
	) {
		this.id = id;
		this.name = name;
		this.productsCount = productsCount;
		this.productGroupsCount = productGroupsCount;
		this.itemsUpdatedTime = itemsUpdatedTime;
		this.notification = notification;
		this.type = type;
		this.systemModule = systemModule;
		this.parent = parent;
		this.exchangeRates = exchangeRates;
		this.historicProductDataViewDate = historicProductDataViewDate;
		this.links = links;
	}

	static empty(id = '') {
		return new UserProductGroup(
			id,
			'',
			0,
			0,
			new Date(0),
			new UserProductGroupNotification(new UserProductGroupNotificationSettings('')),
			'',
			null,
			null,
			new ExchangeRates(new RatesSettings('', '', null, null, TimePeriod.daily), []),
			null,
			{}
		);
	}

	notificationSettingsUpdated(settings: UserProductGroupNotificationSettings): UserProductGroup {
		return new UserProductGroup(
			this.id,
			this.name,
			this.productsCount,
			this.productGroupsCount,
			this.itemsUpdatedTime,
			this.notification.settingsUpdated(settings),
			this.type,
			this.systemModule,
			this.parent,
			this.exchangeRates,
			this.historicProductDataViewDate,
			this.links
		);
	}

	productGroupsCountAndItemsUpdatedTimeUpdated(productGroupsCount: number, itemsUpdatedTime: Date): UserProductGroup {
		return new UserProductGroup(
			this.id,
			this.name,
			this.productsCount,
			productGroupsCount,
			itemsUpdatedTime,
			this.notification,
			this.type,
			this.systemModule,
			this.parent,
			this.exchangeRates,
			this.historicProductDataViewDate,
			this.links
		);
	}

	productsCountAndItemsUpdatedTimeUpdated(productsCount: number, itemsUpdatedTime: Date): UserProductGroup {
		return new UserProductGroup(
			this.id,
			this.name,
			productsCount,
			this.productGroupsCount,
			itemsUpdatedTime,
			this.notification,
			this.type,
			this.systemModule,
			this.parent,
			this.exchangeRates,
			this.historicProductDataViewDate,
			this.links
		);
	}

	exchangeRatesUpdated(exchangeRates: ExchangeRates): UserProductGroup {
		return new UserProductGroup(
			this.id,
			this.name,
			this.productsCount,
			this.productGroupsCount,
			this.itemsUpdatedTime,
			this.notification,
			this.type,
			this.systemModule,
			this.parent,
			exchangeRates,
			this.historicProductDataViewDate,
			this.links
		);
	}

	historicProductDataViewDateUpdated(historicProductDataViewDate: Date | null): UserProductGroup {
		return new UserProductGroup(
			this.id,
			this.name,
			this.productsCount,
			this.productGroupsCount,
			this.itemsUpdatedTime,
			this.notification,
			this.type,
			this.systemModule,
			this.parent,
			this.exchangeRates,
			historicProductDataViewDate,
			this.links
		);
	}

	isProductList(): boolean {
		return this.type === PRODUCT_LIST_TYPE;
	}

	isGroup(): boolean {
		return this.type === GROUP_TYPE;
	}

	hasCzMaxPriceModule(): boolean {
		return this.hasModuleType(ModuleType.czMaxPrice);
	}

	hasCzReimbursementModule(): boolean {
		return this.hasModuleType(ModuleType.czReimbursement);
	}

	hasSkMaxPriceModule(): boolean {
		return this.hasModuleType(ModuleType.skMaxPrice);
	}

	hasRoMaxPriceModule(): boolean {
		return this.hasModuleType(ModuleType.roMaxPrice);
	}

	hasModuleType(type: ModuleType): boolean {
		return this.moduleType === type;
	}

	get moduleType(): ModuleType | null {
		return this.systemModule !== null ? this.systemModule.type : null;
	}

	hasNotificationSettingsLink(): boolean {
		return this.links.notificationSettings !== undefined;
	}

	hasProductsHistory(): boolean {
		return this.links.productsHistory !== undefined;
	}

	hasCzMaxPriceLink(): boolean {
		return this.links.czMaxPriceExport !== undefined;
	}

	hasCzMaxPriceExportLink(): boolean {
		return this.hasCzMaxPriceLink() && this.hasCzMaxPriceModule();
	}

	hasCzReimbursementLink(): boolean {
		return this.links.czReimbursement !== undefined;
	}

	hasCzReimbursementExportLink(): boolean {
		return this.links.czReimbursementExport !== undefined || (this.isProductList() && this.hasCzReimbursementModule());
	}

	hasSkMaxPriceLink(): boolean {
		return this.links.skMaxPrice !== undefined;
	}

	hasSkMaxPriceCNRefExportLink(): boolean {
		return this.links.skMaxPriceCNRefExport !== undefined || (this.isProductList() && this.hasSkMaxPriceModule());
	}

	hasSkMaxPriceCNRefPzluExportLink(): boolean {
		return this.links.skMaxPriceCNRefPzluExport !== undefined || (this.isProductList() && this.hasSkMaxPriceModule());
	}

	hasSkMaxPriceRawExportLink(): boolean {
		return this.links.skMaxPriceRawExport !== undefined || (this.isProductList() && this.hasSkMaxPriceModule());
	}

	hasRoMaxPriceExportLink(): boolean {
		return this.hasRoMaxPriceModule();
	}
}
